import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Trash2, Link2, Unlink, FileText, Send, ChevronLeft, Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { openInvoicePdf } from "@/lib/downloads";
import { eur } from "@/lib/constants";
import { fmtDateTime } from "@/lib/datetime";
import { toast } from "sonner";

const emptyRecord = {
  profile_type: "particulier",
  first_name: "", last_name: "",
  address_line1: "", address_line2: "", postal_code: "", city: "", country: "France",
  billing_email: "", phone: "",
  company_name: "", trade_name: "",
  siren: "", siret: "", vat_number: "", no_vat: false,
  contacts: [{ name: "", email: "" }, { name: "", email: "" }],
  internal_note: "",
  attested: true,
};

function Field({ label, required, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `field-${testId}` : undefined}>
      <span className="cs-eyebrow text-neutral-500">{label}{required ? " *" : ""}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="mt-1 block text-[11px] font-mono text-neutral-500">{hint}</span>}
    </label>
  );
}

export function RecordForm({ value, onChange, testPrefix = "cr", simple = false }) {
  const isPro = value.profile_type === "professionnel";
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setC = (i, k, v) => {
    const nc = [...(value.contacts || [])];
    while (nc.length < 2) nc.push({ name: "", email: "" });
    nc[i] = { ...nc[i], [k]: v };
    onChange({ ...value, contacts: nc });
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[{ v: "particulier", l: "Particulier" }, { v: "professionnel", l: "Professionnel / entreprise" }].map(o => (
          <button key={o.v} type="button" onClick={() => set("profile_type", o.v)}
            className={`flex-1 px-4 py-3 text-sm border ${value.profile_type === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
            data-testid={`${testPrefix}-type-${o.v}`}>{o.l}</button>
        ))}
      </div>
      {isPro && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Raison sociale" required><input className="cs-input" value={value.company_name} onChange={e => set("company_name", e.target.value)} data-testid={`${testPrefix}-company_name`} /></Field>
          <Field label="Nom commercial"><input className="cs-input" value={value.trade_name} onChange={e => set("trade_name", e.target.value)} /></Field>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={isPro ? "Prénom du contact" : "Prénom"} required><input className="cs-input" value={value.first_name} onChange={e => set("first_name", e.target.value)} data-testid={`${testPrefix}-first_name`} /></Field>
        <Field label={isPro ? "Nom du contact" : "Nom"} required><input className="cs-input" value={value.last_name} onChange={e => set("last_name", e.target.value)} data-testid={`${testPrefix}-last_name`} /></Field>
      </div>
      <Field label="Adresse de facturation" required><input className="cs-input" value={value.address_line1} onChange={e => set("address_line1", e.target.value)} data-testid={`${testPrefix}-address_line1`} /></Field>
      <Field label="Complément d'adresse"><input className="cs-input" value={value.address_line2} onChange={e => set("address_line2", e.target.value)} /></Field>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Code postal" required><input className="cs-input" value={value.postal_code} onChange={e => set("postal_code", e.target.value)} data-testid={`${testPrefix}-postal_code`} /></Field>
        <Field label="Ville" required><input className="cs-input" value={value.city} onChange={e => set("city", e.target.value)} data-testid={`${testPrefix}-city`} /></Field>
        <Field label="Pays" required><input className="cs-input" value={value.country} onChange={e => set("country", e.target.value)} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Email de facturation" required><input type="email" className="cs-input" value={value.billing_email} onChange={e => set("billing_email", e.target.value)} data-testid={`${testPrefix}-billing_email`} /></Field>
        <Field label="Téléphone"><input className="cs-input" value={value.phone} onChange={e => set("phone", e.target.value)} /></Field>
      </div>
      {isPro && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="SIREN" required><input className="cs-input" maxLength={9} value={value.siren} onChange={e => set("siren", e.target.value.replace(/\D/g, ""))} data-testid={`${testPrefix}-siren`} /></Field>
          <Field label="SIRET" required><input className="cs-input" maxLength={14} value={value.siret} onChange={e => set("siret", e.target.value.replace(/\D/g, ""))} data-testid={`${testPrefix}-siret`} /></Field>
          <div className="sm:col-span-2">
            <Field label="N° TVA intracommunautaire"><input className="cs-input" value={value.vat_number} onChange={e => set("vat_number", e.target.value.toUpperCase())} placeholder="FR..." disabled={value.no_vat} /></Field>
            <label className="mt-2 flex items-center gap-2 text-[13px]"><input type="checkbox" checked={value.no_vat} onChange={e => set("no_vat", e.target.checked)} /> Pas de n° TVA intracom.</label>
          </div>
        </div>
      )}
      {!simple && <div className="border-t border-black/10 pt-4">
        <div className="cs-eyebrow text-[#0E7C90] mb-2">Contacts rattachables (2 max)</div>
        <p className="text-[12px] text-neutral-500 mb-3">Vous pouvez saisir librement les noms et adresses, même sans compte inscrit. Le rattachement des comptes se fait ensuite manuellement par recherche d'e-mail.</p>
        {[0, 1].map(i => (
          <div key={i} className="grid sm:grid-cols-2 gap-3 mb-2">
            <Field label={`Contact ${i + 1} — nom`}><input className="cs-input" value={value.contacts?.[i]?.name || ""} onChange={e => setC(i, "name", e.target.value)} data-testid={`${testPrefix}-contact-${i}-name`} /></Field>
            <Field label={`Contact ${i + 1} — e-mail`}><input type="email" className="cs-input" value={value.contacts?.[i]?.email || ""} onChange={e => setC(i, "email", e.target.value)} data-testid={`${testPrefix}-contact-${i}-email`} /></Field>
          </div>
        ))}
      </div>}
      {!simple && <Field label="Note interne (admin)"><textarea rows={2} className="cs-input" value={value.internal_note} onChange={e => set("internal_note", e.target.value)} /></Field>}
    </div>
  );
}

function InvoiceEditor({ record, services, onCreated, onCancel }) {
  const [mode, setMode] = useState("forfait");
  const [serviceSlug, setServiceSlug] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [lines, setLines] = useState([{ designation: "", quantity: 1, unit_price: 0 }]);
  const [forfaitAmount, setForfaitAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const svc = services.find(s => s.slug === serviceSlug);
  const autoTotal = useMemo(() => lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0), [lines]);

  const pickService = (slug) => {
    setServiceSlug(slug);
    const s = services.find(x => x.slug === slug);
    if (s) {
      setServiceName(s.name);
      // Prefill first line with service starting price
      setLines(prev => {
        const nl = [...prev];
        if (!nl[0].designation) nl[0].designation = s.name;
        if (!Number(nl[0].unit_price)) nl[0].unit_price = s.starting_price || 0;
        return nl;
      });
    }
  };

  const addLine = () => setLines([...lines, { designation: "", quantity: 1, unit_price: 0 }]);
  const removeLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const submit = async () => {
    if (!record) return;
    setBusy(true);
    try {
      const payload = {
        client_record_id: record.id,
        service_name: serviceName.trim() || (svc?.name ?? "Prestation"),
        service_slug: serviceSlug || null,
        pricing_mode: mode,
        lines: lines.map(l => ({ designation: l.designation, quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0 })),
        forfait_amount: mode === "forfait" ? Number(forfaitAmount) : null,
        notes,
      };
      const { data } = await api.post("/admin/invoices/manual", payload);
      toast.success(`Facture ${data.number} créée`);
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="cs-card p-6" data-testid="invoice-editor">
      <div className="flex items-center justify-between">
        <h3 className="cs-h3 text-xl">Nouvelle facture</h3>
        <button onClick={onCancel} className="text-[13px] font-mono text-neutral-500 hover:text-black">✕ Fermer</button>
      </div>
      <div className="mt-2 text-[13px] text-neutral-600">Fiche : <strong>{record.company_name || `${record.first_name} ${record.last_name}`.trim()}</strong> · {record.billing_email}</div>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <Field label="Prestation (configurateur)" hint="Prépare la ligne à partir du tarif configuré. Facultatif.">
          <select className="cs-input" value={serviceSlug} onChange={e => pickService(e.target.value)} data-testid="invoice-service">
            <option value="">— Aucune / prestation libre —</option>
            {services.filter(s => s.active !== false).map(s => (
              <option key={s.slug} value={s.slug}>{s.name} · à partir de {s.starting_price} €</option>
            ))}
          </select>
        </Field>
        <Field label="Libellé de prestation sur la facture" required>
          <input className="cs-input" value={serviceName} onChange={e => setServiceName(e.target.value)} placeholder="Ex. : Mise en page — 44 pages" data-testid="invoice-service-name" />
        </Field>
      </div>

      <div className="mt-6">
        <div className="cs-eyebrow text-[#0E7C90] mb-2">Mode de facturation</div>
        <div className="flex gap-2">
          {[{ v: "auto", l: "Tarif du configurateur" }, { v: "forfait", l: "Forfait personnalisé" }].map(o => (
            <button key={o.v} type="button" onClick={() => setMode(o.v)}
              className={`flex-1 px-4 py-3 text-sm border ${mode === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25"}`}
              data-testid={`invoice-mode-${o.v}`}>{o.l}</button>
          ))}
        </div>
        {mode === "forfait" && (
          <p className="mt-2 text-[12px] text-neutral-500">Le PDF affichera uniquement ce montant, sans mention du calcul théorique ni ligne de remise automatique.</p>
        )}
      </div>

      {mode === "auto" && (
        <div className="mt-4">
          <div className="cs-eyebrow text-neutral-500 mb-2">Lignes de facturation</div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_120px_40px] gap-2 items-center">
                <input className="cs-input" placeholder="Désignation" value={l.designation} onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, designation: e.target.value } : x))} data-testid={`invoice-line-${i}-designation`} />
                <input type="number" step="0.5" className="cs-input" placeholder="Qté" value={l.quantity} onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} data-testid={`invoice-line-${i}-qty`} />
                <input type="number" step="0.01" className="cs-input" placeholder="P.U. €" value={l.unit_price} onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} data-testid={`invoice-line-${i}-pu`} />
                <button type="button" onClick={() => removeLine(i)} disabled={lines.length <= 1} className="text-neutral-500 hover:text-red-600 disabled:opacity-30"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine} className="mt-2 text-[12px] font-mono uppercase tracking-[0.14em] text-[#0E7C90]">+ Ajouter une ligne</button>
          <div className="mt-3 text-right font-mono text-lg">Total calculé : {eur(autoTotal)}</div>
        </div>
      )}
      {mode === "forfait" && (
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Libellé du forfait (sur le PDF)"><input className="cs-input" value={lines[0]?.designation || ""} onChange={e => setLines([{ ...lines[0], designation: e.target.value }])} placeholder={serviceName || "Prestation forfaitaire"} data-testid="invoice-forfait-label" /></Field>
          <Field label="Montant du forfait (€)" required>
            <input type="number" step="1" className="cs-input" value={forfaitAmount} onChange={e => setForfaitAmount(e.target.value)} placeholder="Ex. 650" data-testid="invoice-forfait-amount" />
          </Field>
        </div>
      )}

      <Field label="Note interne (non imprimée)"><textarea rows={2} className="cs-input" value={notes} onChange={e => setNotes(e.target.value)} /></Field>

      <div className="mt-4 p-3 bg-neutral-50 border border-black/10 text-[12px] text-neutral-600">
        Aucune TVA n'est ajoutée (franchise en base — art. 293 B du CGI). Numérotation automatique. Facture immuable une fois émise.
      </div>

      <div className="mt-4 flex gap-2">
        <button onClick={submit} disabled={busy || (mode === "forfait" && !Number(forfaitAmount)) || !serviceName.trim()} className="cs-btn-primary disabled:opacity-40" data-testid="invoice-create">
          {busy && <Loader2 size={14} className="animate-spin" />} Émettre la facture
        </button>
        <button onClick={onCancel} className="cs-btn-ghost !text-black !border-black">Annuler</button>
      </div>
    </div>
  );
}

function InvoiceSendPanel({ invoice, record, onSent }) {
  const contacts = record?.contacts || [];
  const [recips, setRecips] = useState(() =>
    (contacts.filter(c => c.email).map(c => ({ name: c.name, email: c.email, selected: true }))
      .concat([{ name: "", email: "", selected: false }])).slice(0, 3)
  );
  const [intro, setIntro] = useState(
    "Vous trouverez votre facture en pièce jointe."
  );
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const selected = recips.filter(r => r.selected && r.email?.trim());
    const missing = recips.filter(r => r.selected && !r.email?.trim());
    if (selected.length === 0) return toast.error("Sélectionnez au moins un destinataire avec une adresse e-mail.");
    if (missing.length > 0) return toast.error("Renseignez l'e-mail des destinataires sélectionnés.");
    setBusy(true);
    try {
      await api.post(`/admin/invoices/${invoice.id}/send`, {
        recipients: selected.map(r => ({ name: r.name, email: r.email })),
        intro_html: intro.replace(/\n/g, "<br/>"),
        include_invite_link: invite,
      });
      toast.success(`Facture envoyée à ${selected.length} destinataire(s)`);
      onSent?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setR = (i, k, v) => setRecips(recips.map((x, j) => j === i ? { ...x, [k]: v } : x));

  return (
    <div className="cs-card p-6" data-testid="invoice-send-panel">
      <h3 className="cs-h3 text-xl">Envoyer la facture {invoice.number} par e-mail</h3>
      <p className="mt-1 text-[13px] text-neutral-500">Le PDF est joint. La pièce jointe reste consultable sans compte.</p>

      <div className="mt-4 space-y-2">
        {recips.map((r, i) => (
          <div key={i} className="grid grid-cols-[24px_1fr_1fr] gap-2 items-center">
            <input type="checkbox" checked={r.selected} onChange={e => setR(i, "selected", e.target.checked)} data-testid={`send-recipient-${i}-check`} />
            <input className="cs-input" placeholder="Nom" value={r.name} onChange={e => setR(i, "name", e.target.value)} data-testid={`send-recipient-${i}-name`} />
            <input type="email" className="cs-input" placeholder="e-mail@…" value={r.email} onChange={e => setR(i, "email", e.target.value)} data-testid={`send-recipient-${i}-email`} />
          </div>
        ))}
        <button onClick={() => setRecips([...recips, { name: "", email: "", selected: true }])} className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#0E7C90]">+ Ajouter un destinataire</button>
      </div>

      <Field label="Texte d'accompagnement (modifiable)">
        <textarea rows={5} className="cs-input" value={intro} onChange={e => setIntro(e.target.value)} data-testid="send-intro" />
      </Field>
      <label className="mt-2 flex items-center gap-2 text-[13px]">
        <input type="checkbox" checked={invite} onChange={e => setInvite(e.target.checked)} data-testid="send-invite" />
        Inclure le lien d'inscription au bas du message
      </label>

      <div className="mt-4 flex gap-2">
        <button onClick={send} disabled={busy} className="cs-btn-primary disabled:opacity-40" data-testid="send-do">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Envoyer par e-mail
        </button>
      </div>
    </div>
  );
}

export default function ClientRecordsTab({ services }) {
  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null); // {record, linked_users, invoice_count}
  const [invoices, setInvoices] = useState([]);
  const [mode, setMode] = useState("list"); // list|create|edit
  const [form, setForm] = useState(emptyRecord);
  const [creating, setCreating] = useState(false);

  // Link account
  const [linkEmail, setLinkEmail] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkResolutions, setLinkResolutions] = useState({}); // {field: "record"|"user"}

  // Invoice editor / send
  const [invoiceEditorOpen, setInvoiceEditorOpen] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);

  const loadRecords = async () => {
    const { data } = await api.get("/admin/client-records");
    setRecords(data.records || []);
  };
  const loadDetail = async (id) => {
    const { data } = await api.get(`/admin/client-records/${id}`);
    setDetail(data);
    // Load invoices tied to this record
    const all = await api.get("/admin/invoices");
    setInvoices((all.data.invoices || []).filter(i => i.client_record_id === id));
  };
  useEffect(() => { loadRecords(); }, []);
  useEffect(() => { if (selected) loadDetail(selected); else { setDetail(null); setInvoices([]); } }, [selected]);

  const save = async () => {
    setCreating(true);
    try {
      const payload = { ...form, contacts: (form.contacts || []).filter(c => (c.name || c.email)) };
      if (mode === "create") {
        const { data } = await api.post("/admin/client-records", payload);
        toast.success("Fiche client créée");
        await loadRecords();
        setSelected(data.id);
      } else {
        await api.patch(`/admin/client-records/${selected}`, payload);
        toast.success("Fiche mise à jour");
        await loadRecords();
        await loadDetail(selected);
      }
      setMode("list");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const previewLink = async () => {
    setLinkPreview(null); setLinkResolutions({});
    try {
      const { data } = await api.post(`/admin/client-records/${selected}/preview-link`, { email: linkEmail });
      setLinkPreview(data);
      // Default resolutions: keep the fiche values
      if (data?.conflicts) {
        setLinkResolutions(Object.fromEntries(Object.keys(data.conflicts).map(k => [k, "record"])));
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const confirmLink = async () => {
    try {
      await api.post(`/admin/client-records/${selected}/link`, {
        email: linkEmail,
        resolutions: linkResolutions,
      });
      toast.success("Compte rattaché — e-mail de notification envoyé");
      setLinkEmail(""); setLinkPreview(null); setLinkResolutions({});
      await loadDetail(selected);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.reason === "billing_conflict") {
        setLinkPreview({ conflicts: detail.conflicts, needs_resolution: true });
        setLinkResolutions(prev => ({
          ...Object.fromEntries(Object.keys(detail.conflicts).map(k => [k, "record"])),
          ...prev,
        }));
      } else toast.error(formatApiError(e));
    }
  };
  const setResolution = (field, source) => setLinkResolutions(prev => ({ ...prev, [field]: source }));
  const unlink = async (email) => {
    if (!window.confirm(`Détacher ${email} ?`)) return;
    try { await api.post(`/admin/client-records/${selected}/unlink`, { email }); await loadDetail(selected); toast.success("Rattachement retiré"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeRecord = async () => {
    if (!window.confirm("Supprimer cette fiche ? Uniquement possible si aucune facture n'y est rattachée.")) return;
    try { await api.delete(`/admin/client-records/${selected}`); toast.success("Fiche supprimée"); setSelected(null); await loadRecords(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const startCreate = () => { setForm(emptyRecord); setMode("create"); setSelected(null); };
  const startEdit = () => {
    if (!detail) return;
    setForm({ ...emptyRecord, ...detail.record, contacts: [
      detail.record.contacts?.[0] || { name: "", email: "" },
      detail.record.contacts?.[1] || { name: "", email: "" },
    ] });
    setMode("edit");
  };

  return (
    <div className="mt-8" data-testid="admin-client-records">
      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        <div>
          <div className="flex items-center justify-between gap-3">
            <h2 className="cs-h3 text-xl">Fiches clients ({records.length})</h2>
            <button onClick={startCreate} className="cs-btn-primary !py-2 !px-3 text-[12px]" data-testid="cr-new"><UserPlus size={13} /> Nouvelle</button>
          </div>
          <div className="mt-4 border-t border-black/15">
            {records.map(r => (
              <button key={r.id} onClick={() => { setSelected(r.id); setMode("list"); }}
                className={`w-full text-left border-b border-black/15 py-3 px-2 text-sm hover:bg-white transition-colors ${selected === r.id ? "bg-white" : ""}`}
                data-testid={`cr-${r.id}`}>
                <div className="font-serif text-lg">{r.company_name || `${r.first_name} ${r.last_name}`.trim() || "Fiche sans nom"}</div>
                <div className="font-mono text-xs text-neutral-500">{r.billing_email} · {r.owner_user_id ? "fiche intermédiaire" : `${r.linked_user_ids?.length || 0} compte(s) rattaché(s)`}</div>
                {r.owner_user_id && <div className="mt-1 text-[11px] font-mono text-[#0E7C90]" data-testid={`cr-owner-${r.id}`}>Créée par l'intermédiaire {r.owner_email}{r.archived ? " · archivée" : ""}</div>}
                {r.siret_duplicate && <div className="mt-1 text-[11px] font-mono text-amber-700" data-testid={`cr-dup-${r.id}`}>SIRET présent sur plusieurs fiches</div>}
              </button>
            ))}
            {records.length === 0 && <div className="py-4 text-neutral-500 text-sm">Aucune fiche. Cliquez sur « Nouvelle » pour commencer.</div>}
          </div>
        </div>

        <div>
          {mode === "create" && (
            <div className="cs-card p-6" data-testid="cr-create-form">
              <div className="flex items-center justify-between">
                <h3 className="cs-h3 text-xl">Nouvelle fiche client</h3>
                <button onClick={() => setMode("list")} className="text-[13px] font-mono text-neutral-500 hover:text-black">✕ Annuler</button>
              </div>
              <div className="mt-4"><RecordForm value={form} onChange={setForm} testPrefix="cr" /></div>
              <div className="mt-4 flex gap-2">
                <button onClick={save} disabled={creating} className="cs-btn-primary" data-testid="cr-save">
                  {creating && <Loader2 size={14} className="animate-spin" />} Enregistrer la fiche
                </button>
              </div>
            </div>
          )}
          {mode === "edit" && (
            <div className="cs-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="cs-h3 text-xl">Modifier la fiche</h3>
                <button onClick={() => setMode("list")} className="text-[13px] font-mono text-neutral-500 hover:text-black">✕ Annuler</button>
              </div>
              <div className="mt-4"><RecordForm value={form} onChange={setForm} testPrefix="cre" /></div>
              <div className="mt-4 flex gap-2">
                <button onClick={save} disabled={creating} className="cs-btn-primary">
                  {creating && <Loader2 size={14} className="animate-spin" />} Enregistrer
                </button>
              </div>
            </div>
          )}
          {mode === "list" && detail && (
            <div className="space-y-6">
              <div className="cs-card p-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="cs-eyebrow text-[#0E7C90]">Fiche client</div>
                    <h3 className="cs-h3 mt-1 text-2xl">{detail.record.company_name || `${detail.record.first_name} ${detail.record.last_name}`.trim()}</h3>
                    <div className="mt-1 text-sm text-neutral-500 font-mono">{detail.record.billing_email} · {detail.record.profile_type}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={startEdit} className="cs-btn-ghost !text-black !border-black !py-2 !px-3 text-[13px]">Modifier</button>
                    <button onClick={removeRecord} className="text-[13px] font-mono text-red-600" data-testid="cr-delete">Supprimer</button>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div><span className="text-neutral-500 text-[11px] font-mono uppercase">Adresse</span><div>{[detail.record.address_line1, detail.record.address_line2, `${detail.record.postal_code} ${detail.record.city}`, detail.record.country].filter(Boolean).join(" · ")}</div></div>
                  {detail.record.profile_type === "professionnel" && <>
                    <div><span className="text-neutral-500 text-[11px] font-mono uppercase">SIRET</span><div>{detail.record.siret || "—"}</div></div>
                    <div><span className="text-neutral-500 text-[11px] font-mono uppercase">TVA</span><div>{detail.record.no_vat ? "Non assujettie" : (detail.record.vat_number || "—")}</div></div>
                  </>}
                </div>
                <div className="mt-4 border-t border-black/10 pt-4">
                  <div className="cs-eyebrow text-neutral-500 mb-2">Contacts</div>
                  {(detail.record.contacts || []).length === 0 && <div className="text-sm text-neutral-500">Aucun contact.</div>}
                  {(detail.record.contacts || []).map((c, i) => (
                    <div key={i} className="text-sm">• {c.name || "(sans nom)"} — <span className="font-mono">{c.email || "(sans e-mail)"}</span></div>
                  ))}
                </div>
              </div>

              <div className="cs-card p-6">
                <div className="cs-eyebrow text-[#0E7C90]">Comptes rattachés ({detail.linked_users.length})</div>
                <div className="mt-3 space-y-2">
                  {detail.linked_users.map(u => (
                    <div key={u.id} className="flex items-center justify-between text-sm border border-black/10 px-3 py-2">
                      <div><strong>{u.first_name} {u.last_name}</strong> · <span className="font-mono">{u.email}</span></div>
                      <button onClick={() => unlink(u.email)} className="text-neutral-500 hover:text-red-600 inline-flex items-center gap-1 text-[12px]"><Unlink size={13} /> Détacher</button>
                    </div>
                  ))}
                  {detail.linked_users.length === 0 && <div className="text-sm text-neutral-500">Aucun compte rattaché pour l'instant.</div>}
                </div>
                <div className="mt-4 border-t border-black/10 pt-4">
                  <div className="cs-eyebrow text-neutral-500 mb-2">Rattacher un compte existant</div>
                  <div className="flex gap-2">
                    <input type="email" className="cs-input flex-1" placeholder="email du compte inscrit" value={linkEmail} onChange={e => setLinkEmail(e.target.value)} data-testid="cr-link-email" />
                    <button onClick={previewLink} disabled={!linkEmail} className="cs-btn-ghost !text-black !border-black" data-testid="cr-link-preview"><Link2 size={13} /> Vérifier</button>
                  </div>
                  {linkPreview?.already_linked && <div className="mt-2 text-[13px] text-neutral-600">Ce compte est déjà rattaché.</div>}
                  {linkPreview && !linkPreview.already_linked && (Object.keys(linkPreview.conflicts || {}).length === 0) && (
                    <div className="mt-2 flex items-center justify-between text-sm border border-[#0E7C90]/40 bg-[#0E7C90]/5 px-3 py-2">
                      <span>Aucun conflit détecté. Compte : <strong>{linkPreview.user?.email}</strong></span>
                      <button onClick={confirmLink} className="cs-btn-primary !py-1 !px-3 text-[12px]" data-testid="cr-link-confirm">Rattacher</button>
                    </div>
                  )}
                  {linkPreview && (Object.keys(linkPreview.conflicts || {}).length > 0) && (
                    <div className="mt-2 border border-amber-500 bg-amber-50 px-3 py-3 text-sm" data-testid="cr-link-conflicts">
                      <div className="flex items-center gap-2 text-amber-800 font-medium"><AlertTriangle size={14} /> Différences détectées — confirmez la valeur à conserver pour chaque champ</div>
                      <div className="mt-3 space-y-2">
                        {Object.entries(linkPreview.conflicts).map(([k, v]) => (
                          <div key={k} className="grid grid-cols-[140px_1fr_1fr] gap-2 items-start text-[12px] border-t border-black/10 pt-2">
                            <div className="font-mono uppercase text-neutral-500">{k}</div>
                            <label className={`px-2 py-1 border cursor-pointer ${linkResolutions[k] === "record" ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>
                              <input type="radio" name={`res-${k}`} checked={linkResolutions[k] === "record"} onChange={() => setResolution(k, "record")} className="mr-1" data-testid={`cr-conflict-${k}-record`} />
                              <span className="text-neutral-500">Fiche</span> <span className="ml-1">{String(v.record || "—")}</span>
                            </label>
                            <label className={`px-2 py-1 border cursor-pointer ${linkResolutions[k] === "user" ? "border-[#0E7C90] bg-[#0E7C90]/10" : "border-black/20 bg-white"}`}>
                              <input type="radio" name={`res-${k}`} checked={linkResolutions[k] === "user"} onChange={() => setResolution(k, "user")} className="mr-1" data-testid={`cr-conflict-${k}-user`} />
                              <span className="text-neutral-500">Compte</span> <span className="ml-1">{String(v.user || "—")}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button onClick={confirmLink} className="cs-btn-primary !py-1 !px-3 text-[12px]" data-testid="cr-link-apply">Rattacher avec ces choix</button>
                        <button onClick={() => { setLinkPreview(null); setLinkResolutions({}); }} className="cs-btn-ghost !text-black !border-black !py-1 !px-3 text-[12px]">Annuler</button>
                      </div>
                      <p className="mt-2 text-[11px] text-neutral-500">Les valeurs choisies sont appliquées à la fiche commune. Les autres comptes rattachés seront alignés automatiquement. Les factures déjà émises ne sont pas modifiées.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="cs-card p-6">
                <div className="flex items-center justify-between">
                  <div className="cs-eyebrow text-[#0E7C90]">Factures ({invoices.length})</div>
                  <button onClick={() => setInvoiceEditorOpen(true)} className="cs-btn-primary !py-2 !px-3 text-[12px]" data-testid="cr-new-invoice"><FileText size={13} /> Nouvelle facture</button>
                </div>
                {invoiceEditorOpen && (
                  <div className="mt-4">
                    <InvoiceEditor record={detail.record} services={services} onCreated={async () => { setInvoiceEditorOpen(false); await loadDetail(selected); }} onCancel={() => setInvoiceEditorOpen(false)} />
                  </div>
                )}
                {sendingInvoice && (
                  <div className="mt-4">
                    <InvoiceSendPanel invoice={sendingInvoice} record={detail.record} onSent={async () => { setSendingInvoice(null); await loadDetail(selected); }} />
                  </div>
                )}
                <div className="mt-4 space-y-2">
                  {invoices.map(i => (
                    <div key={i.id} className="grid grid-cols-[140px_1fr_100px_auto_auto] gap-3 items-center text-sm border border-black/10 px-3 py-2" data-testid={`cr-invoice-${i.number}`}>
                      <div className="font-mono">{i.number}</div>
                      <div className="truncate">{i.service_name} <span className="text-[11px] text-neutral-500">· {i.pricing_mode === "forfait" ? "forfait" : "auto"}</span></div>
                      <div className="text-right font-mono">{eur(i.amount)}</div>
                      <button onClick={() => openInvoicePdf(i.id)} className="cs-btn-ghost !text-black !border-black !py-1 !px-3 text-[12px]">PDF</button>
                      <button onClick={() => setSendingInvoice(i)} className="cs-btn-ghost !text-black !border-black !py-1 !px-3 text-[12px]"><Send size={12} /> Envoyer</button>
                    </div>
                  ))}
                  {invoices.length === 0 && <div className="text-sm text-neutral-500">Aucune facture pour cette fiche.</div>}
                </div>
              </div>
            </div>
          )}
          {mode === "list" && !detail && (
            <div className="text-sm text-neutral-500 border border-dashed border-black/25 p-6">Sélectionnez une fiche ou créez-en une nouvelle.</div>
          )}
        </div>
      </div>
    </div>
  );
}
